import { E as EVENTS } from "./events.Dnaewmpi.min.js";
import { S as sectionsApiService } from "./sections-api-service.smvSsykG.min.js";
import { p as parseHtml } from "./parse-html.C-hh7jJd.min.js";
import {
  stripRedundantPriceParamsFromCurrentLocation,
  stripRedundantPriceParamsFromUrlObject,
  syncPriceSearchParamFromNumberInput,
} from "./price-filter-url.js";

class FiltersFacets extends HTMLElement {
  constructor() {
    super();

    this.url = new URL(window.location.href);
    this.filterInputs = this.querySelectorAll("[data-filter-input]");
    this.filterChips = document.querySelectorAll("[data-filter-chips]");
    this.applyButton = document.querySelector("[data-filter-apply]");
    this.timer = undefined;

    // Keep stable references so removeEventListener actually works.
    this.onRender = this.render.bind(this);
    this.onChange = this.handleChange.bind(this);
    this.onClick = this.handleClick.bind(this);
    this.onScroll = this.handleScroll.bind(this);
    this.onSearchUpdate = this.handleSearchUpdate.bind(this);
  }

  connectedCallback() {
    document.addEventListener(EVENTS.FILTER_UPDATE, this.onRender);
    this.addEventListener("change", this.onChange);
    this.addEventListener("click", this.onClick);
    window.addEventListener("scroll", this.onScroll);
    document.addEventListener(EVENTS.SEARCH_UPDATE, this.onSearchUpdate);

    this.syncViewToggleState();
  }

  disconnectedCallback() {
    document.removeEventListener(EVENTS.FILTER_UPDATE, this.onRender);
    this.removeEventListener("change", this.onChange);
    this.removeEventListener("click", this.onClick);
    window.removeEventListener("scroll", this.onScroll);
    document.removeEventListener(EVENTS.SEARCH_UPDATE, this.onSearchUpdate);
  }

  handleClick(event) {
    const toggleButton = event.target.closest("[data-view-toggle]");
    if (!toggleButton) {
      return;
    }

    const mainProductGrid = this.closest("main-product-grid");
    const productGrid = mainProductGrid?.querySelector("[data-product-grid]");
    if (!productGrid) {
      return;
    }

    productGrid.classList.toggle("product-grid--compact");
    this.syncViewToggleState();
  }

  syncViewToggleState() {
    const toggleButton = this.querySelector("[data-view-toggle]");
    const mainProductGrid = this.closest("main-product-grid");
    const productGrid = mainProductGrid?.querySelector("[data-product-grid]");

    if (!toggleButton || !productGrid) {
      return;
    }

    const isCompactView = productGrid.classList.contains("product-grid--compact");
    toggleButton.setAttribute("aria-pressed", `${isCompactView}`);
  }

  handleScroll() {
    const { top } = this.getBoundingClientRect();
    this.setAttribute("data-is-scrolled", top <= 0);
  }

  handleChange(event) {
    clearTimeout(this.timer);

    const inputName = event.target.name;
    const inputValue = event.target.value;
    const isChecked = event.target.checked;
    const isSizeSelection =
      event.target instanceof HTMLInputElement &&
      event.target.type === "checkbox" &&
      inputName.includes("size") &&
      isChecked;
    const nextUrl = new URL(window.location.href);

    // Any filter/sort change must return pagination to the first page.
    nextUrl.searchParams.delete("page");

    if (event.target instanceof HTMLInputElement) {
      if (event.target.type === "checkbox") {
        if (isChecked) {
          // For availability, replace rather than accumulate — prevents auto-applied
          // value from combining with the user's manual selection
          if (inputName === "filter.v.availability") {
            nextUrl.searchParams.delete(inputName);
          }
          nextUrl.searchParams.append(inputName, inputValue);
        } else {
          const otherValues = nextUrl.searchParams
            .getAll(inputName)
            .filter((value) => value !== inputValue);

          nextUrl.searchParams.delete(inputName);
          otherValues.forEach((value) => nextUrl.searchParams.append(inputName, value));
        }
      }

      if (event.target.type === "number") {
        if (!syncPriceSearchParamFromNumberInput(nextUrl, event.target)) {
          nextUrl.searchParams.set(inputName, inputValue);
        }
      }
    }

    if (event.target instanceof HTMLSelectElement) {
      nextUrl.searchParams.set(inputName, inputValue);
    }

    if (isSizeSelection) {
      nextUrl.searchParams.delete("filter.v.availability");
      nextUrl.searchParams.append("filter.v.availability", "1");
    }

    stripRedundantPriceParamsFromUrlObject(nextUrl);

    window.history.replaceState(null, "", nextUrl);

    this.timer = setTimeout(() => {
      document.dispatchEvent(new CustomEvent(EVENTS.FILTER_UPDATE));
    }, 200);
  }

  async handleSearchUpdate() {
    const currentUrl = new URL(window.location.href);
    const html = await sectionsApiService.fetch(currentUrl, "main-product-grid");
    const parsedFacets = parseHtml(html, "filters-facets");

    this.innerHTML = parsedFacets.innerHTML;
    this.syncViewToggleState();
  }

  async render() {
    const currentUrl = new URL(window.location.href);
    const fetchedUrl = currentUrl.toString();
    const html = await sectionsApiService.fetch(currentUrl, "main-product-grid");

    // Discard stale results: URL changed while fetch was in flight (e.g. user
    // unchecked "In Stock" while the auto-apply render was still loading).
    if (window.location.href !== fetchedUrl) {
      return;
    }

    const parsedInputs = parseHtml(html, "filters-facets [data-filter-input]", true);

    this.filterInputs = this.querySelectorAll("[data-filter-input]");

    this.filterInputs.forEach((filterInput) => {
      const priceSlider = filterInput.querySelector("price-slider");
      const currentInput = filterInput.querySelector("input");
      const currentCount = filterInput.querySelector("[data-count]");

      const nextFilterInput = Array.from(parsedInputs).find((parsedInput) => {
        const parsedField = parsedInput?.querySelector("input");
        return parsedField?.id === currentInput?.id;
      });

      if (priceSlider && nextFilterInput) {
        const nextSlider = nextFilterInput.querySelector("price-slider");
        if (
          nextSlider &&
          typeof priceSlider.applyFacetStateFromParsed === "function"
        ) {
          priceSlider.applyFacetStateFromParsed(nextSlider);
          return;
        }
      }

      const nextInput = nextFilterInput?.querySelector("input");
      const nextCount = nextFilterInput?.querySelector("[data-count]");

      if (currentInput && nextInput) {
        currentInput.disabled = nextInput.disabled;
        if (currentInput.type === "checkbox") {
          currentInput.checked = nextInput.checked;
        } else if (currentInput.type === "number") {
          currentInput.value = nextInput.value;
        }
      }

      if (currentCount && nextCount) {
        currentCount.innerText = nextCount.innerText;
      }
    });

    stripRedundantPriceParamsFromCurrentLocation();
  }
}

const init = () => {
  if (!customElements.get("filters-facets")) {
    customElements.define("filters-facets", FiltersFacets);
  }
};

init();

export { FiltersFacets, init as default };
